import React, { useEffect, useState } from 'react'
import { useAppSelector, useAppDispatch } from 'app/hooks'
import {
    showPrompt,
} from 'actions/appActions'
import {
    retrieveData,
    resetAdminError,
    removeData,
} from 'actions/adminActions'
import {
    Button,
    Input,
    Spinner,
    Icon,
    DropdownMenu,
    Checkbox,
    Tile,
    Media,
} from 'components'
import { TooltipLabel } from 'components/Core/TooltipLabel'
import {
    AdminView,
    PromptOptions,
    RootState,
    NestedDict,
    FieldType,
    ValidateType,
    ThumbSize,
    PromptType,
} from 'app/types'
import { validateEmail, validateUsername, validatePassword, validatePasswordConfirm } from 'helpers/authHeader'
import { logger } from 'helpers/logger'
import * as fnc from 'helpers/fnc'
import { AdminTray } from 'views/AdminPage/AdminTray'
import { GalleryTile } from 'components'

const validationOperations: { [key: string]: (x: string) => boolean } = {}
validationOperations[ValidateType.Email] = validateEmail
validationOperations[ValidateType.Name] = validateUsername
validationOperations[ValidateType.Password] = validatePassword
validationOperations[ValidateType.PasswordMatch] = validatePasswordConfirm

interface AdminEditorProps {
    app: AppData,
    organization?: any,
    view: AdminView,
    validation: () => boolean,
    delta: NestedDict,
    onChange: () => void,
    onSubmit: () => void,
    checkChange: () => void,
    newData: Dict,
    editData?: Dict, // Add editData prop
    fields: { [key: string]: FieldDef },
    fieldFilter: () => void,
    reference: () => JSX.Element,
    compactMode?: boolean, // Add compact mode flag
}

export function AdminEditor(props: AdminEditorProps) {
    const {
        app,
        organization,
        view,
        validation,
        delta,
        onChange,
        onSubmit,
        checkChange,
        newData,
        fields,
        fieldFilter,
        reference,
    } = props

    let title = view.toTitleCase()
    let items = title.plural()
    let item = title.singular()

    const dispatch = useAppDispatch()
    let fixedView = view
    if (view == AdminView.AllProjects) {
        fixedView = 'apps'
    } else if (view == AdminView.AllOrganizations) {
        fixedView = 'organizations'
    }

    // Fix: Use editData prop if provided, otherwise fall back to Redux state
    const data = props.editData ? [props.editData] : (props.data ? props.data : useAppSelector((state: RootState) => state.admin[fixedView]))
    const adminError = useAppSelector((state: RootState) => state.admin.error[view])
    const config = useAppSelector((state: RootState) => state.admin.config)
    let viewKey = view
    if (view == AdminView.AllProjects) {
        viewKey = 'apps'
    } else if (view == AdminView.AllOrganizations) {
        viewKey = 'organizations'
    }

    const deltaData = useAppSelector((state: RootState) => state.admin.delta[viewKey])

    const [loaded, setLoaded] = useState(0)
    const [editData, setEditData] = useState(null)
    const [error, setError] = useState(null)
    const [editFields, setEditFields] = useState(null)
    const [searchQuery, setSearchQuery] = useState('')
    const [resettingError, setResettingError] = useState(false)

    useEffect(() => {
        if (Array.isArray(deltaData) && !Array.isArray(editData)) {
            // Else find entry in delta and update edit
            if (editData && editData.id) {
                const editIdx = deltaData.findIndex((x) => x.id == editData.id)
                if (editIdx != -1) {
                    const newEdit = fnc.copyObj(deltaData[editIdx])
                    setEditData(newEdit)
                }
            }
        } else if (Array.isArray(deltaData) == Array.isArray(editData)) {
            setEditData(deltaData)
        }
    }, [deltaData])

    useEffect(() => {
        const asyncLoad = async () => {
            if (!props.data) {
                await dispatch(retrieveData({ view }))
                setLoaded(1)
                setTimeout(() => {
                    setLoaded(2)
                }, 500)
            } else {
                setLoaded(2)
            }
        }
        asyncLoad().catch(logger.error)
    }, [])

    // Auto-select editData when provided as prop (for project-only mode)
    useEffect(() => {
        if (props.editData && loaded >= 2) {
            // Only call handleSelectData if the ID is different from current selection
            // This prevents infinite loops when the same data prop is passed multiple times
            if (!editData || !editData.id || editData.id !== props.editData.id) {
                handleSelectData(props.editData)
            }
        }
    }, [props.editData?.id, loaded])

    function handleSelectData(focus, force = false) {
        // Duplicate to edit
        if (focus) {
            // Skip if selecting the same item (avoid infinite loops from prop updates)
            // Check both id equality and whether this is meaningful new data
            if (editData && editData.id && focus.id && editData.id === focus.id) {
                console.log('DEBUG: Skipping handleSelectData - same item already selected', { existingId: editData.id, incomingId: focus.id })
                return
            }
            const newData = fnc.copyObj(focus)
            setEditData(newData)
            if (fieldFilter) {
                const finalFields = {}
                setEditFields(Object.keys(fields).filter((x) => (fields[x].edit == null || fields[x].edit) && fieldFilter(newData, x)))
            } else {
                setEditFields(Object.keys(fields).filter((x) => fields[x].edit == null || fields[x].edit))
            }
            // Only reset error when actually selecting new data
            dispatch(resetAdminError(view))
        } else {
            if (force) {
                setEditData(null)
                setEditFields(null)
            } else {
                checkChange()
                    .then((x) => {
                        setEditData(null)
                        setEditFields(null)
                    })
            }
            // Only reset error when actually deselecting
            dispatch(resetAdminError(view))
        }
    }

    function handleNew() {
        const newItem = { ...newData }
        handleSelectData(newItem)
    }

    function handleDelete() {
        const deleteOptions = { ...PromptOptions.DeleteData }
        deleteOptions.title = deleteOptions.title.replace('data', item)
        const pendingOptions = { ...PromptOptions.DeletingData }
        pendingOptions.title = pendingOptions.title.replace('data', item)
        pendingOptions.successMessage = pendingOptions.successMessage.replace('Data', item)
        dispatch(showPrompt(deleteOptions))
            .then((x) => {
                if (x.payload) {
                    return Promise.all([
                        dispatch(showPrompt(pendingOptions)),
                        dispatch(removeData({ view, id: editData.id })),
                    ])
                }
                throw x
            })
            .then((x) => {
                setTimeout(() => {
                    handleSelectData(null)
                }, 0)
            })
            .catch(logger.error)
    }

    function handleSave() {
        // Verify
        const validate = validation ? validation(editData) : null
        if (validate) {
            // TODO, Set admin error, display on user view
            setError(validate.map((x, ix) => <span key={ix}>{x}</span>))
        } else {
            setError(null)
            if (onSubmit) {
                onSubmit()
                    .then((x) => {
                        if (x) {
                            handleSelectData(null, true)
                        }
                    })
            }
        }
    }

    function handleEdit(newData = null) {
        if (editData && newData) {
            const finalData = { ...editData, ...newData }
            const change = JSON.stringify(editData) !== JSON.stringify(finalData)
            if (onChange) {
                if (change) {
                    onChange(finalData)
                } else {
                    // onChange(null)
                }
            }
            setEditData(finalData)
        }
    }

    function handleMultiAdd(field: string) {
        const delta = {}
        delta[field] = [...editData[field]]
        delta[field].push(fields[field].default)
        handleEdit(delta)
    }

    function handleMultiRemove(field: string, ix: number) {
        const delta = {}
        delta[field] = [...editData[field]]
        delta[field].splice(ix, 1)
        handleEdit(delta)
    }

    function handleCompoundAdd(field: string) {
        const delta = {}
        delta[field] = fields[field].default
        handleEdit(delta)
    }

    function handleCompoundRemove(field: string) {
        const delta = {}
        delta[field] = null
        handleEdit(delta)
    }

    function handleMedia(field: string, index: number, subkey: string = null) {
        let mediaOptions: any = { app }
        if (view == AdminView.AllProjects) {
            mediaOptions.app = { meta: editData }
        } else if (view == AdminView.Builders) {
            mediaOptions.builder = editData
        } else if (view == AdminView.Organizations) {
            mediaOptions.organization = editData
        } else if (!app && organization) {
            mediaOptions = { organization }
        }

        dispatch(showPrompt({ type: PromptType.Media, selected: [editData[field]], ...mediaOptions }))
            .then((x) => {
                if (x.payload) {
                    if (x.payload.length > 0) {
                        handleFieldChange(field, x.payload[0], index, subkey)
                    } else {
                        handleFieldChange(field, null, index, subkey)
                    }
                }
            })
    }

    function handleGallery(field: string, index: number) {
        let mediaOptions = { app }
        if (view == AdminView.AllProjects) {
            mediaOptions.app = editData
        } else if (view == AdminView.Builders) {
            mediaOptions.builder = editData
        } else if (view == AdminView.Organizations) {
            mediaOptions.organization = editData
        }
        dispatch(showPrompt({ type: PromptType.Gallery, selected: editData[field], ...mediaOptions }))
            .then((x) => {
                if (x.payload !== undefined && x.payload != false) {
                    if (x.payload != 'none') {
                        handleFieldChange(field, x.payload, index)
                    } else if (x.payload == 'none') {
                        handleFieldChange(field, null, index)
                    }
                }
            })
    }

    function handleFieldChange(field: string, value: string, index: number = null, subkey: string = null) {
        const delta = {}
        if (subkey != null) {
            if (index != null) {
                delta[field] = [...editData[field]]
                delta[field][index] = { ...delta[field][index], [subkey]: value }
            } else {
                delta[field] = { ...editData[field] }
                delta[field][subkey] = value
            }
        } else {
            delta[field] = value
        }
        handleEdit(delta)
    }

    function handleListToggle(field: string, value: string) {
        const index = editData[field].findIndex((x) => x == value)
        const newData = [...editData[field]]
        if (index !== -1) {
            newData.splice(index, 1)
        } else {
            newData.push(value)
        }
        const delta = {}
        delta[field] = newData
        handleEdit(delta)
    }

    function handleLocation(field: string, key: string, value: string) {
        dispatch(showPrompt({ type: PromptType.Location, app: view == AdminView.AllProjects ? editData : null, organization: view == AdminView.Organizations ? editData : null, selected: value, select: true, ...field }))
            .then((x) => {
                if (x.payload) {
                    handleFieldChange(key, x.payload)
                } else if (x.payload !== false) {
                    handleFieldChange(key, null)
                }
            })
    }

    function getListHeader(key: string) {
        const field = fields[key]
        if (field.label) {
            return field.label
        }
        return key.toTitleCase()
    }

    function getFieldOptions(field) {
        if(!field.options) {
            return []
        }
        if (typeof field.options === 'function') {
            return field.options(editData)
        } else {
            return field.options
        }
    }

    function getListField(row, key: string) {
        const field = fields[key]
        const type = field.type
        if (!(key in row)) {
            return <h4>Missing field {key}</h4>
        }
        switch (type) {
            case FieldType.String:
                return row[key]
            case FieldType.Integer:
                return row[key] != null ? row[key].toLocaleString() : ''
            case FieldType.Float:
                return row[key] != null ? row[key] : ''
            case FieldType.Toggle:
                return row[key] ? <Icon noBg alt icon="fas fa-check" /> : null
            case FieldType.List:
                return `${row[key].length} - ${key}`
            case FieldType.Option:
                if (!field.options) {
                    return ''
                }
                let option = getFieldOptions(field)?.find((x) => x.id == row[key])
                if (option) {
                    return option.name
                }
                return 'Not found'
            case FieldType.Date:
                return fnc.dateFriendly(new Date(row[key]))
            case FieldType.Media:
                if (row[key]) {
                    let mediaOptions: any = { app }
                    if (view == AdminView.AllProjects) {
                        mediaOptions.app = { meta: row }
                    } else if (view == AdminView.Builders) {
                        mediaOptions.builder = row
                    } else if (view == AdminView.Organizations) {
                        mediaOptions.organization = row
                    } else if (!app && organization) {
                        mediaOptions = { organization }
                    }
                    return <div key={row[key]} className="tile-list by-1" style={{ pointerEvents: 'none' }}>
                        <Tile square className="media-tile"><Media key={row[key]} mediaId={row[key]} thumb thumbSize={ThumbSize.Medium} hoverShow {...mediaOptions} /></Tile>
                    </div>
                }
        }
    }

    function getInputField(key: string) {
        const field = fields[key]
        if (field.element) {
            return field.element
        }
        if (field.map) {
            return field.map(editData, handleFieldChange)
        }
        if (!(key in editData)) {
            return <h4>Missing field {key}</h4>
        }
        // Show static if not new
        if (field.fixedOnUpdate && editData.id != null) {
            return editData[key]
        }
        // Show disabled message
        if (field.disable) {
            const disable = field.disable(editData)
            if (disable) {
                return disable
            }
        }
        const getField = (field, value, index = null, subkey = null) => {
            const type = field.type
            const title = field.label || key.toTitleCase()
            const elemKey = `${key}_${index}_${subkey}`
            switch (type) {
                default:
                    return null
                case FieldType.String:
                    return <Input phone={field.validation == ValidateType.Phone} textarea={field.textarea} placeholder={field.placeholder || `Enter ${title}`} label={subkey ? field.label : null} name={field.name} value={value} onChange={(x) => handleFieldChange(key, x, index, subkey)} />
                case FieldType.Integer:
                    return <Input type="number" placeholder={field.placeholder || `Enter ${title}`} label={subkey ? field.label : null} name={field.name} value={value} onChange={(x) => handleFieldChange(key, x, index, subkey)} />
                case FieldType.Float:
                    return <Input type="number" step="0.01" placeholder={field.placeholder || `Enter ${title}`} label={subkey ? field.label : null} name={field.name} value={value ?? ''} onChange={(x) => handleFieldChange(key, x, index, subkey)} />
                case FieldType.Toggle:
                    return <Checkbox noBg alt name={key} value={value} onChange={(x) => handleFieldChange(key, x, index)}>{field.text}</Checkbox>
                case FieldType.List: {
                    if (!field.options) {
                        return <h4>Missing options</h4>
                    }

                    let finalOptions = getFieldOptions(field)
                    if (field.filter) {
                        finalOptions = finalOptions.filter((x) => field.filter(x, editData))
                    }

                    return <div className="admin-data-list">
                        {value.map((x) => {
                            const data = finalOptions.find((y) => y.id == x)
                            if (data) {
                                let text = ''
                                if (data) {
                                    if (field.optionMap) {
                                        text = field.optionMap(data).text
                                    } else if (typeof data == 'object' && 'name' in data) {
                                        text = data.name
                                    } else {
                                        text = option
                                    }
                                    return <Button key={data.id} className="admin-data-list-item selected" icon="fas fa-times" onClick={() => handleListToggle(key, data.id)}>{text}</Button>
                                }
                            }
                            return <h3 key={x}> Missing Data {x}</h3>
                        })}
                        {finalOptions.length > 0 && <DropdownMenu
                            scrollable
                            text={`Add ${title}`}
                            items={finalOptions.filter((x) => {
                                return !value.find((y) => (field.selector ? field.selector(x) : x) == y)

                            }).map((x) => {
                                if (field.optionMap) {
                                    return field.optionMap(x)
                                } else if (typeof x == 'object' && 'id' in x) {
                                    return { text: x.name, value: x.id }
                                } else {
                                    return { text: x, value: x }
                                }
                            })}
                            hideOnEmpty
                            onChange={(x) => handleListToggle(key, x, index, subkey)} />}
                    </div>
                }
                case FieldType.Option:
                    if (!field.options) {
                        return <h4>Missing options</h4>
                    }

                    let finalOptions = getFieldOptions(field)
                    if (field.filter) {
                        finalOptions = finalOptions.filter((x) => field.filter(x, editData))
                    }

                    const option = value != null ? finalOptions.find((x) => (typeof x == 'object' && x.id == value) || x == value) : null
                    let text = ''
                    if (option) {
                        if (field.optionMap) {
                            text = field.optionMap(option).text
                        } else if (typeof option == 'object' && 'name' in option) {
                            text = option.name
                        } else {
                            text = option
                        }
                    } else {
                        text = `Select ${title}`
                    }
                    return <div className="admin-data-list">
                        <DropdownMenu
                            scrollable
                            text={text}
                            value={value}
                            items={getFieldOptions(field)?.filter((x) => true || x.id != value).map((x) => {
                                if (field.optionMap) {
                                    return field.optionMap(x)
                                } else if (typeof x == 'object' && 'id' in x) {
                                    return { text: x.name, value: x.id }
                                } else {
                                    return { text: x, value: x }
                                }
                            })}
                            closeOnSelect={field.closeOnSelect}
                            onChange={(x) => handleFieldChange(key, x, index, subkey)} />
                    </div>
                case FieldType.Media:
                    if (!editData.id) {
                        return <div className="tile-list by-1" style={{ opacity: 0.5 }}>
                            <h5>Save entry to edit media</h5>
                        </div>
                    }

                    if (value) {
                        let mediaOptions: any = { app }
                        if (view == AdminView.AllProjects) {
                            mediaOptions.app = { meta: editData }
                        } else if (view == AdminView.Builders) {
                            mediaOptions.builder = editData
                        } else if (view == AdminView.Organizations) {
                            mediaOptions.organization = editData
                        } else if (!app && organization) {
                            mediaOptions = { organization }
                        }

                        return <div className="tile-list by-1" key={elemKey}>
                            <Tile square className="media-tile"><Media key={value} mediaId={value} onClick={() => handleMedia(key, index)} thumb thumbSize={ThumbSize.ExtraLarge} hoverShow {...mediaOptions} /></Tile>
                        </div>
                    }
                    return <div className="tile-list by-1" key={elemKey}>
                        <Tile square className="adder" onClick={() => handleMedia(key, index, subkey)}>
                            <Button icon="fas fa-plus no-bg" >Select {title}</Button>
                        </Tile>
                    </div>
                case FieldType.Gallery:
                    if (value) {
                        let galleries = []
                        let mediaOptions = { app }
                        if (app) {
                            galleries = app.galleries
                        } else if (editData.galleries != null) {
                            galleries = editData.galleries
                            if (view == AdminView.AllProjects) {
                                mediaOptions.app = { meta: editData }
                            } else if (view == AdminView.Builders) {
                                mediaOptions.builder = editData
                            } else if (view == AdminView.Organizations) {
                                mediaOptions.organization = editData
                            }
                        }
                        const gallery = galleries.find((x) => x.id == value)
                        return <div className="tile-list by-1" key={elemKey}>
                            <GalleryTile gallery={gallery} onClick={() => handleGallery(key, index)} {...mediaOptions} />
                            {/*<Tile square className="adder" onClick={() => handleGallery(key, index)}>
                                        <Button icon="fas fa-pen" >Edit {title}</Button>
                            </Tile>*/}
                        </div>
                    }
                    return <div className="tile-list by-1" key={elemKey}>
                        <Tile square className="adder" onClick={() => handleGallery(key, index)}>
                            <Button icon="fas fa-plus" >Add {title}</Button>
                        </Tile>
                    </div>
                case FieldType.Location:
                    if (value) {
                        const location = editData.locations.find((x) => x.id == value)
                        return <Button alt onClick={() => handleLocation(field, key, value)}>{location?.name ? location.name : 'Missing location name'}</Button>
                    }
                    return <Button alt onClick={() => handleLocation(field, key, value)}>Manage Locations</Button>
            }
        }
        if (field.type == FieldType.Compound && field.compoundMap) {
            // Return 
            if (field.multi) {
                return <React.Fragment>
                    <div className="multi">
                        {editData[key].map((x, ix) => {
                            const fields = field.compoundMap(x)
                            return <div className="compound-field">
                                <Button alt className="remove" onClick={() => handleMultiRemove(key, ix)}>Remove</Button>
                                <h5>{field.addLabel}: {ix + 1}</h5>
                                {Object.keys(fields).map((y) => getField(fields[y], editData[key][ix][y], ix, y))}
                            </div>
                        })}
                    </div>
                    <Button alt onClick={() => handleMultiAdd(key)}>Add {field.addLabel}</Button>
                </React.Fragment>
            } else {
                const fields = editData[key] ? field.compoundMap(editData[key]) : null
                return <React.Fragment>
                    {fields && <div className="compound-field">
                        <Button alt className="remove" onClick={() => handleCompoundRemove(key)}>Remove</Button>
                        <h5>{field.addLabel}</h5>
                        {Object.keys(fields).map((y) => getField(fields[y], editData[key][y], null, y))}
                    </div>}
                    {editData[key] == null && <Button alt onClick={() => handleCompoundAdd(key)}>Add {field.addLabel}</Button>}
                </React.Fragment>
            }
        } else {
            return getField(field, editData[key])
        }
    }
    function getDataTable() {
        if (!data) {
            return null
        }
        if (Array.isArray(data) && data.length == 0) {
            return <React.Fragment>
                <div className="admin-table-wrapper">
                    <div className="admin-table-topbar">
                        <h2 className="admin-topbar-title">{title}</h2>
                        <div className="admin-table-options">
                            <Button onClick={handleNew} icon="fas fa-plus" >New {item}</Button>
                        </div>
                    </div>
                </div>
                <div className="column">
                    <h3 style={{ margin: '20px auto' }}>No {items} created yet</h3>
                    <Button style={{ margin: '0 auto' }} onClick={handleNew} icon="fas fa-plus" >New {item}</Button>
                </div>
            </React.Fragment>
        }
        const listFields = Object.keys(fields).filter((x) => fields[x].list == null || fields[x].list)

        // Filter data based on search query
        const filteredData = Array.isArray(data) ? data.filter((row) => {
            if (!searchQuery.trim()) return true
            const query = searchQuery.toLowerCase()
            return listFields.some((fieldKey) => {
                const value = row[fieldKey]
                if (value == null) return false
                if (typeof value === 'string') {
                    return value.toLowerCase().includes(query)
                }
                if (typeof value === 'number') {
                    return value.toString().includes(query)
                }
                return false
            })
        }) : data

        return <React.Fragment>
            <div className="admin-table-wrapper">
                <div className="admin-table-topbar">
                    <h2 className="admin-topbar-title">{title}</h2>
                    <div className="admin-table-search">
                        <Input
                            placeholder={`Search ${items}...`}
                            value={searchQuery}
                            onChange={(x) => setSearchQuery(x)}
                            icon="fas fa-search"
                        />
                    </div>
                    <div className="admin-table-options">
                        <Button onClick={handleNew} icon="fas fa-plus" >New {item}</Button>
                    </div>
                </div>
                <div className="row admin-table full clickable fadeIn">
                    <table>
                        <thead>
                            <tr>
                                {listFields.map((x, ix) => <th key={ix}>{getListHeader(x)}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {Array.isArray(filteredData) && filteredData.length > 0 && filteredData.map((x) => <tr
                                key={x.id}
                                onClick={() => handleSelectData(x)}>
                                {listFields.map((y, iy) => <td key={iy}>{getListField(x, y)}</td>)}
                            </tr>)}
                            {Array.isArray(filteredData) && filteredData.length === 0 && searchQuery && <tr>
                                <td colSpan={listFields.length} style={{ textAlign: 'center', padding: '20px' }}>
                                    No {items.toLowerCase()} found matching "{searchQuery}"
                                </td>
                            </tr>}
                        </tbody>
                    </table>
                </div>
            </div>
        </React.Fragment>
    }

    function getEditor() {
        if (editFields == null) {
            return null
        }
        const isNew = editData.id == null
        // const editFields = Object.keys(fields).filter((x) => fields[x].edit == null || fields[x].edit)
        return <div className="admin-editor">
            <div className="column fadeIn">
                <div className="row admin-title">
                    <h2>{isNew ? `Create ${item}` : `Edit ${item}`}</h2>
                </div>
                {error && <div className="row">
                    <div className="error-list error">{error}</div>
                </div>}
                {adminError && <div className="row">
                    <div className="error-list error"><span>{adminError}</span></div>
                </div>}
                <div className="admin-table" style={{ overflow: 'visible' }}>
                    <table key={editData.id} className="headless">
                        <tbody>
                            {editFields.map((x) => <React.Fragment key={x}>
                                {fields[x].header && <tr>
                                    <th className="field-header" colSpan="2">{fields[x].header}</th>
                                </tr>}
                                <tr>
                                    <th>
                                        <TooltipLabel tooltip={fields[x].tooltip} htmlFor={x}>
                                            {fields[x].label || x.toTitleCase()}
                                        </TooltipLabel>
                                    </th>
                                    <td>{getInputField(x)}</td>
                                </tr>
                            </React.Fragment>)}
                        </tbody>
                    </table>
                </div>
            </div>
            {editData.id != null && <Button icon="fas fa-trash-alt" onClick={handleDelete}>Delete {item}</Button>}
        </div>
    }

    function getCompactEditor() {
        if (editFields == null) {
            return null
        }

        return <div className="admin-editor compact">
            <div className="column fadeIn">
                {error && <div className="row">
                    <div className="error-list error">{error}</div>
                </div>}
                {adminError && <div className="row">
                    <div className="error-list error"><span>{adminError}</span></div>
                </div>}
                <div className="admin-table" style={{ overflow: 'visible' }}>
                    <table key={editData.id} className="headless">
                        <tbody>
                            {editFields.map((x) => <React.Fragment key={x}>
                                {fields[x].header && <tr>
                                    <th className="field-header" colSpan="2">{fields[x].header}</th>
                                </tr>}
                                <tr>
                                    <th>
                                        <TooltipLabel tooltip={fields[x].tooltip} htmlFor={x}>
                                            {fields[x].label || x.toTitleCase()}
                                        </TooltipLabel>
                                    </th>
                                    <td>{getInputField(x)}</td>
                                </tr>
                            </React.Fragment>)}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    }

    function getContent() {
        if (data && data.error) {
            return <React.Fragment>
                <div className="row admin-title">
                    <h2>{items}</h2>
                </div>
                <h3>{data.error}</h3>
            </React.Fragment>
        }

        if (editData && Object.keys(editData).length > 0) {
            // In compact mode, show just the form fields without navigation
            if (props.compactMode) {
                return <React.Fragment>
                    {getCompactEditor()}
                    {reference && reference()}
                </React.Fragment>
            }

            // Standard mode without AdminTray (will be placed outside editor-view)
            return <React.Fragment>
                {getEditor()}
                {reference && reference()}
            </React.Fragment>
        }
        return getDataTable()
    }

    let adminTray = null
    if (editData && Object.keys(editData).length > 0 && !props.compactMode) {
        adminTray = <AdminTray message={`${editData.id == null ? `Create ${item}` : 'Save Changes'}`} onBack={() => handleSelectData(null, false)} onSave={handleSave} />
    }

    return <React.Fragment>
        {adminTray}
        <div id="editor-view" className="admin-content fadeIn">
            {loaded < 2 && <Spinner show={loaded === 0} showAfter={0} />}
            {loaded >= 2 && getContent()}
        </div>
    </React.Fragment>
}
